import React, { useState } from "react";
import { 
  CheckCircle, 
  ArrowRight, 
  User, 
  Building2, 
  ShieldCheck, 
  Globe, 
  Zap, 
  Users,
  Award,
  ExternalLink,
  Sparkles
} from "lucide-react";
import MembershipForm from "./MembershipForm";

export default function JoinUs() {
  const [showModal, setShowModal] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [selectedFee, setSelectedFee] = useState("");

  const handleJoin = (type, fee) => {
    setSelectedType(type);
    setSelectedFee(fee);
    setShowModal(true);
  };

  const partners = [
    { name: "Strategic Partner 1", logo: "/images/logo1.png" },
    { name: "Strategic Partner 2", logo: "/images/logo2.png" },
    { name: "Strategic Partner 3", logo: "/images/logo3.png" },
    { name: "Strategic Partner 4", logo: "/images/logo4.png" },
    { name: "Strategic Partner 5", logo: "/images/logo5.png" },
    { name: "Strategic Partner 6", logo: "/images/logo6.png" },
    { name: "Strategic Partner 7", logo: "/images/logo7.png" },
    { name: "Strategic Partner 8", logo: "/images/logo8.png" },
  ];

  return (
    <div className="min-h-screen bg-[#fcfdfe] text-slate-900 font-['Outfit'] selection:bg-blue-500 selection:text-white">
      {/* Background Mesh Gradients for Richness */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-100/50 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-100/50 blur-[120px] rounded-full" />
      </div>

      {/* Hero Section */}
      <section className="relative py-32 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm mb-8 animate-fade-in">
            <Sparkles className="w-4 h-4 text-blue-600" />
            <span className="text-[10px] font-black tracking-[0.3em] uppercase text-slate-500">Official Enrollment Gateway</span>
          </div>
          <h1 className="text-6xl md:text-8xl font-black tracking-tighter mb-8 text-slate-900">
            Shape the Future <br className="hidden md:block"/> <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">of Synergy.</span>
          </h1>
          <p className="text-xl md:text-2xl text-slate-500 max-w-3xl mx-auto font-light leading-relaxed">
            Join a prestigious network of visionary leaders and organizations 
            driving the India-Europe economic corridor forward.
          </p>
        </div>
      </section>

      {/* Membership Selection */}
      <section className="max-w-7xl mx-auto px-6 pb-32 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          
          {/* Individual / Volunteer */}
          <div className="group relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-[2.5rem] blur opacity-10 group-hover:opacity-30 transition duration-1000"></div>
            <div className="relative h-full bg-white border border-slate-100 p-12 rounded-[2.5rem] flex flex-col shadow-[0_15px_50px_-12px_rgba(0,0,0,0.05)] hover:shadow-[0_30px_70px_-12px_rgba(59,130,246,0.15)] transition-all duration-700">
              <div className="flex justify-between items-start mb-12">
                <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform duration-500 shadow-inner">
                  <User size={32} />
                </div>
                <div className="text-right">
                  <span className="block text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Status</span>
                  <span className="text-2xl font-black text-slate-900">Open Access</span>
                </div>
              </div>

              <h3 className="text-4xl font-black mb-4 tracking-tight">Individual Member</h3>
              <p className="text-slate-500 mb-10 text-lg leading-relaxed">Perfect for professionals, students, and consultants looking to expand their global reach.</p>

              <div className="space-y-5 mb-12 flex-grow">
                {[
                  "Access to global networking forums",
                  "Monthly industry synergy reports",
                  "Priority registration for webinars",
                  "Digital certificate of membership",
                  "Exclusive project collaboration"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4 text-slate-600 group/item">
                    <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center group-hover/item:bg-blue-600 transition-colors">
                      <CheckCircle className="w-4 h-4 text-blue-600 group-hover/item:text-white transition-colors" />
                    </div>
                    <span className="font-medium">{item}</span>
                  </div>
                ))}
              </div>

              <button 
                onClick={() => handleJoin("Volunteer", "Free")}
                className="w-full py-5 bg-slate-900 text-white rounded-2xl font-bold text-lg flex items-center justify-center gap-3 hover:bg-blue-600 transition-all duration-300 group/btn shadow-xl shadow-slate-200 hover:shadow-blue-200"
              >
                Join as Individual
                <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-2 transition-transform" />
              </button>
            </div>
          </div>

          {/* Organization */}
          <div className="group relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-pink-500 rounded-[2.5rem] blur opacity-10 group-hover:opacity-30 transition duration-1000"></div>
            <div className="relative h-full bg-white border border-slate-100 p-12 rounded-[2.5rem] flex flex-col shadow-[0_15px_50px_-12px_rgba(0,0,0,0.05)] hover:shadow-[0_30px_70px_-12px_rgba(147,51,234,0.15)] transition-all duration-700">
              <div className="flex justify-between items-start mb-12">
                <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center text-purple-600 group-hover:scale-110 transition-transform duration-500 shadow-inner">
                  <Building2 size={32} />
                </div>
                <div className="text-right">
                  <span className="block text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Enrollment</span>
                  <span className="text-2xl font-black text-slate-900">€100.00</span>
                </div>
              </div>

              <h3 className="text-4xl font-black mb-4 tracking-tight">Organization</h3>
              <p className="text-slate-500 mb-10 text-lg leading-relaxed">For businesses, NGOs, and institutions seeking B2B growth and policy influence.</p>

              <div className="space-y-5 mb-12 flex-grow">
                {[
                  "Official Institutional Partner status",
                  "Exclusive B2B Matchmaking services",
                  "Featured company profile on portal",
                  "Voting rights in annual assemblies",
                  "Direct trade mission connectivity",
                  "Logo branding at all major events"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4 text-slate-600 group/item">
                    <div className="w-6 h-6 rounded-full bg-purple-50 flex items-center justify-center group-hover/item:bg-purple-600 transition-colors">
                      <CheckCircle className="w-4 h-4 text-purple-600 group-hover/item:text-white transition-colors" />
                    </div>
                    <span className="font-medium">{item}</span>
                  </div>
                ))}
              </div>

              <button 
                onClick={() => handleJoin("Organization", "€100.00")}
                className="w-full py-5 bg-purple-600 text-white rounded-2xl font-bold text-lg flex items-center justify-center gap-3 hover:bg-slate-900 transition-all duration-300 group/btn shadow-xl shadow-purple-100 hover:shadow-slate-200"
              >
                Enroll Organization
                <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-2 transition-transform" />
              </button>
            </div>
          </div>

        </div>
      </section>

      {/* Partners Section - Rich Light Theme */}
      <section className="bg-slate-50/50 border-t border-slate-100 py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h4 className="text-[10px] font-black tracking-[0.4em] uppercase text-slate-400 mb-4">Strategic Alliance Network</h4>
            <h2 className="text-4xl font-black text-slate-900">Collaborating with the World's Best</h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
             {partners.map((p, i) => (
               <div 
                key={i} 
                className="group/partner bg-white border border-slate-200/50 rounded-2xl p-8 flex items-center justify-center h-44 hover:shadow-xl hover:shadow-slate-200/50 hover:-translate-y-1 transition-all duration-500"
               >
                  <img 
                    src={p.logo} 
                    alt={p.name} 
                    className="max-h-full max-w-full object-contain grayscale opacity-60 group-hover/partner:grayscale-0 group-hover/partner:opacity-100 transition-all duration-700" 
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/200x100/f8fafc/64748b?text=PARTNER+' + (i+1) }}
                  />
               </div>
             ))}
          </div>

          <div className="mt-20 text-center">
            <p className="text-slate-500 flex flex-col md:flex-row items-center justify-center gap-2">
              <span>Interested in becoming a strategic partner?</span>
              <a href="mailto:support@pie-es.com" className="text-blue-600 font-bold hover:underline flex items-center gap-1 group">
                Contact our alliance team 
                <ExternalLink size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Modal with Membership Form */}
      {showModal && (
        <MembershipForm 
          setShowModal={setShowModal} 
          selectedMembershipType={selectedType}
          selectedMembershipFee={selectedFee}
        />
      )}
    </div>
  );
}